import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AreaResponse } from '../../lib/areas';
import { areasApi } from '../../lib/areas';
import { presetsApi } from '../../lib/presets';
import { usageApi, type AreaPresetState } from '../../lib/usage';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import SetPresetDurationDialog from '../presets/SetPresetDurationDialog';

/** Row represents an open timed session for what the area currently shows */
function isOpenUsageRowForCurrentPreset(
  s: AreaPresetState,
  currentPresetId: string | null,
): boolean {
  if (s.active !== true) return false;
  if (s.endTime != null && s.endTime !== '') return false;
  if (currentPresetId == null || currentPresetId === '') return false;
  return s.presetId === currentPresetId;
}

interface AreaControlProps {
  area: AreaResponse;
  onSetCurrentPreset: (areaId: string, presetId: string | null, presetAddress?: number | null) => void;
  disabled?: boolean;
  /** When false, preset buttons are hidden (controller offline/warning). */
  controllerOnline?: boolean;
  /** When set, site-level usage queries are invalidated after preset changes */
  siteId?: string;
}

export default function AreaControl({
  area,
  onSetCurrentPreset,
  disabled = false,
  controllerOnline = false,
  siteId,
}: AreaControlProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isSetPresetModalOpen, setIsSetPresetModalOpen] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState<number | ''>(30);
  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: 'Notice', message: '' });
  const [pendingPresetSelection, setPendingPresetSelection] = useState<{
    areaId: string;
    presetId: string;
    presetAddress?: number | null;
  } | null>(null);

  const { data: activeAreaPresetStates } = useQuery({
    queryKey: ['area-preset-states', 'area', area.id, 'open'],
    queryFn: () => usageApi.getEvents({ areaId: area.id, active: true }),
    enabled: !!area.id,
    refetchInterval: 60_000,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403) return false;
      return failureCount < 3;
    },
  });

  const scheduledEndSummary = useMemo(() => {
    const rows = (activeAreaPresetStates ?? []).filter((s) =>
      isOpenUsageRowForCurrentPreset(s, area.currentPresetId),
    );
    if (rows.length === 0) return null;
    const withSchedule = rows
      .map((s) => s.scheduledEndTime)
      .filter((t): t is string => !!t);
    if (withSchedule.length === 0) return null;
    const earliestMs = Math.min(...withSchedule.map((t) => new Date(t).getTime()));
    return new Date(earliestMs);
  }, [activeAreaPresetStates, area.currentPresetId]);

  const { data: presets, isLoading: presetsLoading, status: presetsStatus } = useQuery({
    // Backend filters `/presets` for end users based on group membership + curfew rules.
    // Include user identity in the cache key so we never mix results between sessions.
    queryKey: ['presets', area.id, user?.id, user?.role],
    queryFn: () => presetsApi.getWithFilter({ areaId: area.id, availableOnly: true, type: 'light' }),
    enabled: !!area.id && controllerOnline,
    retry: (failureCount, error: unknown) => {
      // Don't retry on 403 (forbidden) errors
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const setCurrentPresetMutation = useMutation({
    mutationFn: ({ areaId, presetId, durationMinutes: duration }: { areaId: string; presetId: string; durationMinutes?: number }) =>
      areasApi.setCurrentPreset(areaId, presetId, duration),
    onSuccess: (updatedArea) => {
      if (updatedArea.warning) {
        setUiAlert({ open: true, title: 'Warning', message: updatedArea.warning });
      }
      // Invalidate areas for the controller that contains this area
      queryClient.invalidateQueries({ queryKey: ['areas', updatedArea.siteControllerId] });
      // Invalidate the specific area
      queryClient.invalidateQueries({ queryKey: ['area', updatedArea.id] });
      // Invalidate presets for this area to refresh any preset-related data
      queryClient.invalidateQueries({ queryKey: ['presets', updatedArea.id] });
      queryClient.invalidateQueries({ queryKey: ['area-preset-states', 'area', updatedArea.id] });
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: ['area-preset-states', siteId] });
      }
      // Call the parent callback if provided
      if (onSetCurrentPreset) {
        const preset = presets?.find((p) => p.id === updatedArea.currentPresetId);
        onSetCurrentPreset(updatedArea.id, updatedArea.currentPresetId, preset?.address ?? null);
      }
      setIsSetPresetModalOpen(false);
      setPendingPresetSelection(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || 'Failed to set current preset';
      setUiAlert({ open: true, title: 'Error', message: errorMessage });
    },
  });

  const presetDurations = [15, 30, 60, 90, 120, 180];
  
  const handleDurationSelect = (value: number) => {
    setDurationMinutes(value);
  };

  const handleCustomDurationChange = (val: string) => {
    if (val === '') {
      setDurationMinutes('');
    } else {
      const parsed = parseInt(val, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        setDurationMinutes(parsed);
      }
    }
  };

  const handleConfirmSetPreset = () => {
    if (!pendingPresetSelection) return;
    const durationValue = durationMinutes === '' ? 0 : durationMinutes;
    setCurrentPresetMutation.mutate({
      areaId: pendingPresetSelection.areaId,
      presetId: pendingPresetSelection.presetId,
      durationMinutes: durationValue && durationValue > 0 ? durationValue : 0,
    });
  };

  const handlePresetClick = (presetId: string, presetAddress?: number | null) => {
    if (disabled || !controllerOnline) return;
    
    // If "All Off" preset (address 4), apply immediately with duration 0
    if (presetAddress === 4) {
      setCurrentPresetMutation.mutate({
        areaId: area.id,
        presetId,
        durationMinutes: 0,
      });
      return;
    }

    setPendingPresetSelection({ areaId: area.id, presetId, presetAddress });
    setIsSetPresetModalOpen(true);
  };

  return (
    <>
    <Card className="bg-muted/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h5 className="font-semibold text-foreground">{area.name}</h5>
          </div>
          {disabled && (
            <Badge variant="secondary" className="text-xs shrink-0">
              Disabled
            </Badge>
          )}
        </div>

        {/* Presets Section */}
        <div className="mt-3 pt-3">
          {!controllerOnline ? (
            <p className="text-xs text-muted-foreground">
              Presets unavailable while the site controller is not online
            </p>
          ) : presetsLoading || presetsStatus === 'pending' ? (
            <p className="text-xs text-muted-foreground">Loading presets...</p>
          ) : presets && presets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {[...presets]
                .map((preset) => {
                const isCurrent = area.currentPresetId === preset.id;
                const showOffAtBadge = isCurrent && scheduledEndSummary;
                // const isAvailable = !preset.availability.hasWindows || preset.availability.isWithinWindow;
                return (
                  <Button
                    key={preset.id}
                    variant={isCurrent ? 'default' : 'outline'}
                    className="w-full justify-between items-center text-left h-auto py-3 gap-2"
                    onClick={() => !isCurrent && handlePresetClick(preset.id, preset.address)}
                    aria-pressed={isCurrent}
                    disabled={disabled}
                  >
                    <span className="text-sm font-medium truncate min-w-0">{preset.dashboardName ?? preset.name}</span>
                    {/* {!isAvailable ? <span className="text-xs text-muted-foreground">Not available</span> : null} */}
                    {showOffAtBadge ? (
                      <Badge
                        variant="secondary"
                        className="text-xs font-semibold tabular-nums shrink-0"
                        title={scheduledEndSummary.toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      >
                        Off at{' '}
                        <time dateTime={scheduledEndSummary.toISOString()}>
                          {scheduledEndSummary.toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </time>
                      </Badge>
                    ) : isCurrent ? (
                      <Badge variant="secondary" className="text-xs ml-2 shrink-0">
                        Active
                      </Badge>
                    ) : null}
                  </Button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No presets available for this area</p>
          )}
        </div>
      </CardContent>

      {isSetPresetModalOpen && pendingPresetSelection && (
        <SetPresetDurationDialog
          open={isSetPresetModalOpen}
          onOpenChange={setIsSetPresetModalOpen}
          durationMinutes={durationMinutes}
          presetDurations={presetDurations}
          onDurationSelect={handleDurationSelect}
          onCustomDurationChange={handleCustomDurationChange}
          onConfirm={handleConfirmSetPreset}
          isSubmitting={setCurrentPresetMutation.isPending}
        />
      )}
    </Card>
    <AlertDialog
      open={uiAlert.open}
      onOpenChange={(open) => setUiAlert((prev) => ({ ...prev, open }))}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
          <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
